import Grid from "@mui/material/Grid";
import {
  Box,
  Alert,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Cancel,
  CheckCircle,
  Search,
  SwitchAccount,
} from "@mui/icons-material";
import React, { useEffect, useMemo, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import { usePagination } from "../../../hooks/usePagination";
import { useApiClient } from "../../../api";
import { useAuthenticationStore } from "../../../stores/auth";
import { checkIfIsSuperuser } from "../../auth/Utils";
import { ActivationEmailDialog } from "./ActivationEmailDialog";
import { StudentWelcomeEmailDialog } from "./StudentWelcomeEmailDialog";
import { useNavigate } from "react-router-dom";
import { useImpersonateUser } from "../hooks/useImpersonateUser";

export function UserSummary({
  user,
  onImpersonateUser,
  allowImpersonate = false,
  hideSubscriptionStatus = false,
  selected = false,
  onSelectChange = null,
}) {
  const navigate = useNavigate();
  const theme = useTheme();
  return (
    <Card
      sx={{ minWidth: 0, width: "100%" }}
      variant="outlined"
      data-testid={`user-summary-${user.id}`}
    >
      <CardContent>
        <Stack
          direction="row"
          sx={{ flexWrap: "wrap", gap: 1 }}
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ minWidth: 0, flex: "1 1 12rem" }}
          >
            {onSelectChange && (
              <Checkbox
                checked={selected}
                onChange={(e) => onSelectChange(user, e.target.checked)}
              />
            )}
            <Typography
              variant="subtitle1"
              sx={{ minWidth: 0, overflowWrap: "anywhere" }}
            >
              {user.username}
            </Typography>
          </Stack>
          {allowImpersonate && (
            <Tooltip
              title={
                !user.isActive
                  ? "User is not active. Unable to impersonate"
                  : ""
              }
            >
              <Box component="span" sx={{ flexShrink: 0 }}>
                <Button
                  endIcon={<SwitchAccount />}
                  variant="card-action"
                  disabled={!user.isActive}
                  onClick={() => onImpersonateUser(user)}
                >
                  Impersonate
                </Button>
              </Box>
            </Tooltip>
          )}
        </Stack>

        <Typography variant="subtitle2">
          <strong>Name:</strong> {user.firstName} {user.lastName}
        </Typography>
        <Stack direction={"row"} spacing={1} alignItems={"center"}>
          <Typography
            variant="subtitle2"
            sx={{ minWidth: 0, overflowWrap: "anywhere" }}
          >
            <strong>Email:</strong> {user.email}{" "}
          </Typography>
          {user.isEmailVerified ? (
            <CheckCircle
              fontSize="small"
              sx={{ color: theme.palette.success.main, flexShrink: 0 }}
            />
          ) : (
            <Cancel
              fontSize="small"
              sx={{ color: theme.palette.error.main, flexShrink: 0 }}
            />
          )}
        </Stack>
        <Typography variant="subtitle2">
          <strong>Active:</strong>{" "}
          {user.isActive === true ? (
            <Chip label="Active" color={"success"} />
          ) : (
            <Chip label="Not active" color={"error"} />
          )}
        </Typography>
        {!hideSubscriptionStatus && (
          <Typography variant="subtitle2">
            <strong>Subscription Status:</strong>{" "}
            <Chip label={user.subscriptionStatus} />
          </Typography>
        )}
        <Stack direction="row" spacing={2} sx={{ mt: 1, mb: 2 }}>
          <Typography variant="subtitle2">Roles: </Typography>
          {user.displayRoles.map((role, idx) => (
            <Chip label={role} key={idx} sx={{ mt: 1, mb: 1 }} />
          ))}
        </Stack>
      </CardContent>
      <CardActions sx={{ justifyContent: "flex-end" }}>
        <Button
          variant={"action"}
          onClick={() => navigate(`/superuser/users/${user.id}`)}
        >
          View
        </Button>
      </CardActions>
    </Card>
  );
}

export const FruitifyUserSearch = ({
  school = null,
  schoolGroup = null,
  showTitle = false,
  hideSubscriptionStatus = false,
  allowEmailStatusFilter = false,
  allowSelect = false,
}) => {
  const [activationDialogOpen, setActivationDialogOpen] = useState(false);
  const [studentWelcomeEmailDialogOpen, setStudentWelcomeEmailDialogOpen] =
    useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedEmailStatus, setSelectedEmailStatus] = useState("");
  const [selectedPseudonymisationStatus, setSelectedPseudonymisationStatus] =
    useState("active");
  const [debouncedValue, setDebouncedValue] = useState(searchTerm);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [availableRoles, setAvailableRoles] = useState([]);
  const { user } = useAuthenticationStore();
  const { impersonateUser } = useImpersonateUser();
  const ApiClient = useApiClient();

  const isSuperuser = checkIfIsSuperuser({ user: user });

  const handleImpersonateClicked = (targetUser) => {
    impersonateUser(targetUser);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(searchTerm);
    }, 200);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  const filters = useMemo(
    () => ({
      searchString: debouncedValue,
      school: school?.id,
      schoolGroup: schoolGroup?.id,
      role: selectedRole,
      subscriptionStatus: selectedStatus,
      emailStatus: selectedEmailStatus,
      pseudonymisationStatus: selectedPseudonymisationStatus,
    }),
    [
      debouncedValue,
      school,
      selectedRole,
      schoolGroup,
      selectedStatus,
      selectedEmailStatus,
      selectedPseudonymisationStatus,
    ],
  );

  const {
    options: users,
    page,
    pageInfo,
    loading,
    error,
    handlePageChange,
  } = usePagination(
    ({ first, after }) =>
      ApiClient.users.allUsers({ first, after, ...filters }),
    20,
    filters,
  );

  useEffect(() => {
    let active = true;
    ApiClient.users
      .getAllAvailableRoles()
      .then((roles) => {
        if (!active) return;
        const options = roles.map(({ code, name }) => ({ code, name }));
        if (!options.some(({ code }) => code === "moderator")) {
          options.push({ code: "moderator", name: "Moderator" });
        }
        setAvailableRoles(options);
      })
      .catch(() => {
        if (active) setAvailableRoles([]);
      });
    return () => {
      active = false;
    };
  }, [ApiClient]);

  const subscriptionStatuses = [
    { value: "approved", label: "Approved" },
    { value: "unapproved", label: "Unapproved" },
  ];

  const handleSelectChange = (user, checked) => {
    setSelectedUsers((prev) => {
      const next = new Set(prev);
      checked ? next.add(user) : next.delete(user);
      return next;
    });
  };

  useEffect(() => {
    if (selectAll) {
      setSelectedUsers(new Set(users));
    } else {
      setSelectedUsers(new Set());
    }
  }, [selectAll, users]);

  // Prune selections when the visible server-filtered page changes.
  useEffect(() => {
    setSelectedUsers((prev) => {
      const next = new Set(
        [...prev].filter((existing) => users.some((u) => u.id === existing.id)),
      );
      return next;
    });
  }, [users]);

  return (
    <>
      <ActivationEmailDialog
        open={activationDialogOpen}
        setOpen={setActivationDialogOpen}
        users={Array.from(selectedUsers)}
      />
      <StudentWelcomeEmailDialog
        open={studentWelcomeEmailDialogOpen}
        setOpen={setStudentWelcomeEmailDialogOpen}
        school={school}
        users={Array.from(selectedUsers)}
      />
      <Grid
        container
        spacing={2}
        sx={{ width: "100%", mt: 2 }}
        data-testid="fruitify-teacher-search"
      >
        {showTitle && (
          <Grid size={12} sx={{ mt: 2 }}>
            <Typography variant={"h5"}>Users</Typography>
          </Grid>
        )}
        <Grid size={12} sx={{ mt: !showTitle ? 2 : 0 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ width: "100%", mb: 2 }}
          >
            <TextField
              label="Search"
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ width: { xs: "100%", sm: 280 } }}
            />
            <FormControl sx={{ width: { xs: "100%", sm: 280 } }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={selectedRole}
                label="Role"
                onChange={(e) => setSelectedRole(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {availableRoles.map((role) => (
                  <MenuItem key={role.code} value={role.code}>
                    {role.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {!hideSubscriptionStatus && (
              <FormControl sx={{ width: { xs: "100%", sm: 280 } }}>
                <InputLabel>School subscription</InputLabel>
                <Select
                  value={selectedStatus}
                  label="School subscription"
                  onChange={(e) => setSelectedStatus(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {subscriptionStatuses.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {allowEmailStatusFilter && (
              <FormControl sx={{ width: { xs: "100%", sm: 280 } }}>
                <InputLabel>Email Status</InputLabel>
                <Select
                  value={selectedEmailStatus}
                  label="Email Status"
                  onChange={(e) => setSelectedEmailStatus(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="verified">Verified</MenuItem>
                  <MenuItem value="unverified">Unverified</MenuItem>
                </Select>
              </FormControl>
            )}
            <FormControl sx={{ width: { xs: "100%", sm: 220 } }}>
              <InputLabel id="user-history-label">User history</InputLabel>
              <Select
                labelId="user-history-label"
                value={selectedPseudonymisationStatus}
                label="User history"
                onChange={(e) =>
                  setSelectedPseudonymisationStatus(e.target.value)
                }
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="historic">Historic</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Grid>
        <Grid size={12} sx={{ mt: !showTitle ? 2 : 0 }}>
          <Stack direction={"row"} spacing={2} alignItems={"center"}>
            {allowSelect && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectAll}
                    onChange={(e) => setSelectAll(e.target.checked)}
                  />
                }
                label={"Select All"}
              />
            )}
            {allowSelect && (
              <FormControl sx={{ width: { xs: "100%", sm: 280 } }}>
                <Button
                  variant={"outlined"}
                  disabled={Array.from(selectedUsers).length === 0}
                  onClick={() => setActivationDialogOpen(true)}
                >
                  Send activation email(s)
                </Button>
              </FormControl>
            )}
            {school && (
              <FormControl sx={{ width: { xs: "100%", sm: 280 } }}>
                <Button
                  variant={"outlined"}
                  onClick={() => setStudentWelcomeEmailDialogOpen(true)}
                >
                  Send student welcome emails
                </Button>
              </FormControl>
            )}
          </Stack>
        </Grid>

        {loading && (
          <Grid size={12} sx={{ display: "flex", justifyContent: "center" }}>
            <CircularProgress aria-label="Loading users" />
          </Grid>
        )}
        {error && (
          <Grid size={12}>
            <Alert severity="error">Unable to load users.</Alert>
          </Grid>
        )}
        {!loading && !error && users.length === 0 && (
          <Grid size={12}>
            <Typography color="text.secondary">No users found.</Typography>
          </Grid>
        )}
        {!loading &&
          users.map((user) => (
            <Grid size={{ xs: 12, md: 4 }} key={user.id}>
              <UserSummary
                user={user}
                onImpersonateUser={handleImpersonateClicked}
                allowImpersonate={isSuperuser}
                hideSubscriptionStatus={hideSubscriptionStatus}
                selected={Array.from(selectedUsers).some(
                  (u) => u.id === user.id,
                )}
                onSelectChange={allowSelect && handleSelectChange}
              />
            </Grid>
          ))}
      </Grid>
      <Box display="flex" justifyContent="center" mt={3}>
        <Pagination
          page={page}
          onChange={handlePageChange}
          count={pageInfo.hasNextPage ? page + 1 : page} // keeps the pagination going
          color="primary"
        />
      </Box>
    </>
  );
};
